import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Clipboard from 'clipboard';
import { Svg } from './Svg.js';
import { KeyboardClickable } from './KeyboardClickable.js';
import { SHARE_TOOLS, trackShareToolClick } from './shareToolUtils.js';

/* global dcAnalytics */

const isClickToCopySupported = () => {
    return document.queryCommandSupported('copy');
};

export const ClickToCopy = ({
    projectId,
    buttonClassName,
    indicatorClassName,
    useSpan,
    textToCopy,
    getTrackingLink,
    onClickToCopy,
    pageName,
    children,
    className,
    essentialsListShare,
}) => {
    const [clicked, setClicked] = useState(false);
    const holderRef = useRef(null);

    useEffect(() => {
        let clipboard;
        if (isClickToCopySupported() && holderRef.current) {
            clipboard = new Clipboard(holderRef.current);
        }

        return () => {
            if (clipboard) {
                clipboard.destroy();
            }
        };
    }, []); // Empty dependency array ensures this runs only once on mount and unmount

    useEffect(() => {
        let timeoutId;
        if (clicked) {
            timeoutId = setTimeout(() => {
                setClicked(false);
            }, 2000);
        }
        return () => {
            clearTimeout(timeoutId);
        };
    }, [clicked]);

    const getTextToCopy = useCallback(() => {
        if (getTrackingLink) {
            return decodeURIComponent(getTrackingLink(textToCopy, 'directlink', pageName));
        }
        return textToCopy;
    }, [getTrackingLink, textToCopy, pageName]);

    const handleClick = (event) => {
        setClicked(true);
        if (onClickToCopy) {
            onClickToCopy();
        }

        dcAnalytics.trackShareToolClick(pageName, textToCopy, 'directlink', essentialsListShare);
        trackShareToolClick(projectId, SHARE_TOOLS.DIRECT_LINK);
        event.preventDefault();
    };

    const handleSpanClick = (event) => {
        handleClick(event);
        event.stopPropagation();
    };

    const renderLink = () => {
        const decoratedTextToCopy = getTextToCopy();
        const linkContent = (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div className="copy-link" onClick={handleClick}>
                <div className={`project-url ${clicked ? 'copied' : ''}`}>
                    <Svg icon="ss-link" iconFamily="social-circle" />
                    <input
                        type="text"
                        className="heap-copylink-share"
                        name="project-url"
                        value={decoratedTextToCopy}
                        readOnly
                    />
                </div>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    ref={holderRef} // Assign the ref to the element
                    data-clipboard-text={decoratedTextToCopy}
                    aria-label="Copy link"
                    className={`${className} heap-copy-share button-secondary small`}
                    title="Copy project link"
                    data-title="Copy project link"
                    href="#"
                >
                    {children}
                </a>
            </div>
        );

        if (useSpan) {
            return (
                <KeyboardClickable>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <div className="copy-link" onClick={handleSpanClick}>
                        {linkContent}
                    </div>
                </KeyboardClickable>
            );
        }
        return linkContent;
    };

    if (!isClickToCopySupported()) {
        return null;
    }

    return (
        <div className="share-wrapper">
            {renderLink()}
        </div>
    );
};

ClickToCopy.propTypes = {
    projectId: PropTypes.number,
    buttonClassName: PropTypes.string,
    indicatorClassName: PropTypes.string,
    useSpan: PropTypes.bool,
    textToCopy: PropTypes.string.isRequired,
    getTrackingLink: PropTypes.func,
    onClickToCopy: PropTypes.func,
    pageName: PropTypes.string,
    children: PropTypes.element,
    className: PropTypes.string,
    essentialsListShare: PropTypes.bool,
};
